import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { ChevronDown, Search as SearchIcon } from 'lucide-react';
import { listBranches, listRepos } from '../lib/api';
import type { SearchFilters } from '../lib/types';

interface Props {
  filters: SearchFilters;
  setFilters: (next: SearchFilters) => void;
}

const ALL_KINDS = ['backend', 'frontend', 'schema', 'bridge'] as const;

export function FiltersSidebar({ filters, setFilters }: Props) {
  const reposQuery = useQuery({
    queryKey: ['repos'],
    queryFn: () => listRepos(),
  });
  const repos = reposQuery.data?.repos ?? [];

  // Languages are derived from what's in the index. For now, hard-code a useful set —
  // adding a /api/languages endpoint is on the polish list (see README).
  const knownLanguages = ['python', 'typescript', 'javascript', 'graphql', 'sql', 'rust', 'go'];

  return (
    <aside className="w-60 shrink-0 border-r border-zinc-200 dark:border-zinc-800 px-3 py-4 overflow-y-auto h-full">
      <h2 className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-2">
        Repos
      </h2>
      <div className="space-y-1 mb-4">
        {reposQuery.isLoading && (
          <div className="text-xs text-zinc-500">Loading…</div>
        )}
        {reposQuery.error && (
          <div className="text-xs text-rose-600">Failed to load repos.</div>
        )}
        {repos.map((r) => {
          const selected = filters.repos.length === 0 || filters.repos.includes(r.repo_name);
          return (
            <label key={r.repo_name} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={selected}
                onChange={(e) => {
                  let next = filters.repos.slice();
                  // Special "(bridges)" isn't in code_rag_meta — it's auto-included when no
                  // repo filter is set. We model "all" as empty list, "specific subset" as
                  // explicit list. Toggling a repo migrates between those two states.
                  if (e.target.checked) {
                    if (filters.repos.length === 0) {
                      // user toggled OFF some repos by re-enabling one — that's actually
                      // already-on; do nothing.
                      return;
                    }
                    next = [...next, r.repo_name];
                    // If we just selected every repo, collapse back to empty (==all).
                    if (next.length === repos.length) next = [];
                  } else {
                    if (filters.repos.length === 0) {
                      // Going from "all" to "all-but-this-one": materialize the explicit list.
                      next = repos.filter((x) => x.repo_name !== r.repo_name).map((x) => x.repo_name);
                    } else {
                      next = next.filter((x) => x !== r.repo_name);
                    }
                  }
                  setFilters({ ...filters, repos: next });
                }}
              />
              <span className="font-mono text-xs truncate">{r.repo_name}</span>
              <span
                className={clsx(
                  'ml-auto text-[10px] uppercase font-medium tracking-wide px-1 py-0.5 rounded',
                  r.repo_kind === 'backend' && 'bg-amber-100 dark:bg-amber-900 text-amber-900 dark:text-amber-100',
                  r.repo_kind === 'frontend' && 'bg-sky-100 dark:bg-sky-900 text-sky-900 dark:text-sky-100',
                  r.repo_kind === 'schema' && 'bg-violet-100 dark:bg-violet-900 text-violet-900 dark:text-violet-100',
                )}
              >
                {r.repo_kind}
              </span>
            </label>
          );
        })}
      </div>

      <h2 className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-2">
        Kinds
      </h2>
      <div className="space-y-1 mb-4">
        {ALL_KINDS.map((kind) => {
          const selected = filters.repo_kinds.length === 0 || filters.repo_kinds.includes(kind);
          return (
            <label key={kind} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={selected}
                onChange={(e) => {
                  let next = filters.repo_kinds.slice();
                  if (e.target.checked) {
                    if (filters.repo_kinds.length === 0) return;
                    next = [...next, kind];
                    if (next.length === ALL_KINDS.length) next = [];
                  } else {
                    if (filters.repo_kinds.length === 0) {
                      next = ALL_KINDS.filter((k) => k !== kind);
                    } else {
                      next = next.filter((k) => k !== kind);
                    }
                  }
                  setFilters({ ...filters, repo_kinds: next });
                }}
              />
              <span className="font-mono text-xs">{kind}</span>
            </label>
          );
        })}
      </div>

      <h2 className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-2">
        Languages
      </h2>
      <div className="space-y-1 mb-4">
        {knownLanguages.map((lang) => {
          const selected = filters.languages.includes(lang);
          return (
            <label key={lang} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={selected}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...filters.languages, lang]
                    : filters.languages.filter((l) => l !== lang);
                  setFilters({ ...filters, languages: next });
                }}
              />
              <span className="font-mono text-xs">{lang}</span>
            </label>
          );
        })}
      </div>

      <h2 className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-2">
        Branches
      </h2>
      <div className="space-y-2 mb-4">
        {repos.map((r) => (
          <BranchPicker
            key={r.repo_name}
            repoName={r.repo_name}
            value={filters.branches[r.repo_name] ?? 'main'}
            onChange={(v) =>
              setFilters({
                ...filters,
                branches: { ...filters.branches, [r.repo_name]: v },
              })
            }
          />
        ))}
      </div>

      <h2 className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-2">
        Min score
      </h2>
      <input
        type="range"
        min="0"
        max="1"
        step="0.05"
        value={filters.min_score}
        onChange={(e) =>
          setFilters({ ...filters, min_score: parseFloat(e.target.value) })
        }
        className="w-full"
      />
      <div className="text-xs text-zinc-500 dark:text-zinc-400 text-right font-mono">
        {filters.min_score.toFixed(2)}
      </div>
    </aside>
  );
}

interface BranchPickerProps {
  repoName: string;
  value: string;
  onChange: (next: string) => void;
}

function BranchPicker({ repoName, value, onChange }: BranchPickerProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const branchesQuery = useQuery({
    queryKey: ['branches', repoName],
    queryFn: () => listBranches(repoName),
    enabled: open, // only fetch when the user opens the dropdown
  });
  const branches = branchesQuery.data?.branches ?? [];
  const options = useMemo(() => {
    const names = new Set<string>(['main']);
    for (const b of branches) names.add(b.branch_name);
    return Array.from(names).filter((n) =>
      filter ? n.toLowerCase().includes(filter.toLowerCase()) : true,
    );
  }, [branches, filter]);

  return (
    <div className="relative">
      <div className="text-xs text-zinc-500 dark:text-zinc-400 font-mono truncate">
        {repoName}:
      </div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-1 rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-2 py-1 text-xs font-mono"
      >
        <span className="truncate">{value || 'main'}</span>
        <ChevronDown size={12} className="opacity-60" />
      </button>
      {open && (
        <div className="absolute z-10 mt-1 left-0 right-0 max-h-64 overflow-auto rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 shadow-md">
          <div className="flex items-center gap-1 border-b border-zinc-200 dark:border-zinc-800 px-2 py-1">
            <SearchIcon size={12} className="opacity-60" />
            <input
              autoFocus
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="filter"
              className="w-full bg-transparent text-xs font-mono outline-none"
            />
          </div>
          {branchesQuery.isLoading && (
            <div className="text-xs px-2 py-1 text-zinc-500">Loading…</div>
          )}
          {options.length === 0 && !branchesQuery.isLoading && (
            <div className="text-xs px-2 py-1 text-zinc-500">
              (no indexed branches — request via webhook or{' '}
              <code className="font-mono">code-rag-index --branch</code>)
            </div>
          )}
          {options.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => {
                onChange(n);
                setOpen(false);
              }}
              className={clsx(
                'block w-full text-left px-2 py-1 text-xs font-mono hover:bg-indigo-100 dark:hover:bg-indigo-950',
                n === value && 'bg-indigo-50 dark:bg-indigo-950',
              )}
            >
              {n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
